"use client";

import { useTranslations } from "next-intl";
import Image, { StaticImageData } from "next/image";

// 新版 Logo 静态导入（使用 new-version/logo 目录）
import logoAbb from "@/../public/image/new-version/logo/abb-2.webp";
import logoBmw from "@/../public/image/new-version/logo/bmw-logo-693-1.webp";
import logoElectrolux from "@/../public/image/new-version/logo/electrolux.webp";
import logoFlir from "@/../public/image/new-version/logo/FLIR-2-11.webp";
import logoGoogle from "@/../public/image/new-version/logo/google-logo.webp";
import logoIdent from "@/../public/image/new-version/logo/i-dent-logo.webp";

// SVG 文件使用字符串路径
const logoBombardier = "/image/new-version/logo/bombardier_.svg";
const logoLamborghini = "/image/new-version/logo/Lamborghini_-_logo_wordmark_1.svg";
const logoTesla = "/image/new-version/logo/Tesla_Motors_Logo_1.svg";

const logos: { src: StaticImageData | string; alt: string }[] = [
	{ src: logoAbb, alt: "ABB" },
	{ src: logoBmw, alt: "BMW" },
	{ src: logoBombardier, alt: "Bombardier" },
	{ src: logoElectrolux, alt: "Electrolux" },
	{ src: logoFlir, alt: "FLIR Systems" },
	{ src: logoGoogle, alt: "Google" },
	{ src: logoIdent, alt: "i-dent" },
	{ src: logoLamborghini, alt: "Lamborghini" },
	{ src: logoTesla, alt: "Tesla" },
];

// 无缝滚动：2 组重复，-50% 位移实现无缝循环
const LOGO_GROUPS = 2;
const allLogosForScroll = Array.from({ length: LOGO_GROUPS }, () => logos).flat();

/** 滚动速度：数值越小越快，如 15 较快、30 较慢，单位秒 */
const SCROLL_DURATION = 30;

interface TrustedCompaniesProps {
	translationNamespace?: string;
}

export default function TrustedCompanies({
	translationNamespace = "trustedCompanies",
}: TrustedCompaniesProps) {
	const t = useTranslations(translationNamespace);

	return (
		<>
			<section className="border-b border-t border-[#7d7d7d] bg-white">
				<div className="container">
					<div className="flex flex-col items-center gap-4 py-4 sm:gap-5 sm:py-0 lg:flex-row lg:gap-12">
						{/* 左侧标题 */}
						<div className="flex shrink-0 items-center border-[#7d7d7d] text-center lg:self-stretch lg:border-r lg:pr-8 lg:text-left">
							<h2 className="text-lg font-bold text-black sm:text-xl md:text-2xl">
								<span className="block">{t("titleLine1")}</span>
								<span className="block">{t("titleLine2")}</span>
							</h2>
						</div>

						{/* 右侧logo滚动区域 */}
						<div className="relative w-full overflow-hidden py-3 sm:py-4 lg:py-6">
							{/* 左侧渐变遮罩 */}
							<div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-8 bg-gradient-to-r from-white to-transparent sm:w-12 lg:w-16" />
							{/* 右侧渐变遮罩 */}
							<div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-8 bg-gradient-to-l from-white to-transparent sm:w-12 lg:w-16" />

							{/* 无限滚动容器：2 组 logos，-50% 位移实现无缝循环，速度由 SCROLL_DURATION 控制 */}
							<div
								className="flex w-max animate-scroll-x gap-6 sm:gap-10 md:gap-14 lg:gap-16"
								style={{ animationDuration: `${SCROLL_DURATION}s` }}
							>
								{allLogosForScroll.map((logo, index) => (
									<div
										key={`logo-${index}`}
										className="flex h-8 w-20 shrink-0 items-center justify-center grayscale transition-all duration-300 hover:grayscale-0 sm:h-10 sm:w-24 md:h-12 md:w-32 lg:h-14 lg:w-36"
									>
										<Image
											src={logo.src}
											alt={logo.alt}
											width={144}
											height={56}
											className="h-full w-full object-contain"
										/>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</section>
			<p className="px-4 py-6 text-center font-sans text-sm font-bold sm:py-8 sm:text-base md:py-10 lg:py-12">
				{t("desc")}
			</p>
		</>
	);
}
